import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { supplierService, Supplier } from "@/services/supplier";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SupplierComboboxProps {
  value: Supplier | null;
  onChange: (supplier: Supplier | null) => void;
  disabled?: boolean;
}

export function SupplierCombobox({ value, onChange, disabled }: SupplierComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: suppliersResponse, isLoading } = useQuery({
    queryKey: ["suppliers", "search", debouncedSearch],
    queryFn: () =>
      supplierService.getAll({
        search: debouncedSearch || undefined,
        limit: 20,
      }),
    enabled: open,
  });

  const suppliers = suppliersResponse?.data || [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full justify-between font-normal"
          disabled={disabled}
        >
          {value ? (
            <span className="flex items-center gap-2 truncate">
              <span>{value.name}</span>
              {value.phone && (
                <span className="text-muted-foreground text-xs">{value.phone}</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">Buscar proveedor...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nombre..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading || search !== debouncedSearch ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : suppliers.length === 0 ? (
              <CommandEmpty>
                {debouncedSearch ? "No se encontraron proveedores" : "Escribe para buscar"}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {suppliers.map((supplier) => (
                  <CommandItem
                    key={supplier.id}
                    value={String(supplier.id)}
                    onSelect={() => {
                      onChange(supplier);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value?.id === supplier.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{supplier.name}</span>
                      {(supplier.phone || supplier.email) && (
                        <span className="text-xs text-muted-foreground">
                          {supplier.phone}
                          {supplier.phone && supplier.email && " • "}
                          {supplier.email}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
